import { BlockShiftPuzzleState } from "@shared/types/BlockShiftPuzzleState";
import { ShiftableBlockState } from "@shared/types/ShiftableBlockState";
import { BaseRouteService } from "./BaseRouteService";

export class BlockShiftPuzzleService extends BaseRouteService {
    private readonly _image: HTMLImageElement;

    public constructor() {
        super();
        this._image = document.querySelector("#blockShiftPuzzlePieces")!;
    }

    private async getState(): Promise<BlockShiftPuzzleState> {
        return await this.getJsonApi<BlockShiftPuzzleState>("game/block-shift-puzzle/state");
    }

    private async postState(mouseX: number, mouseY: number): Promise<BlockShiftPuzzleState> {
        return await this.postJsonApi<BlockShiftPuzzleState, { mouseX: number; mouseY: number }>("game/block-shift-puzzle/action", {
            mouseX,
            mouseY,
        });
    }

    private async reset(): Promise<BlockShiftPuzzleState> {
        return await this.getJsonApi<BlockShiftPuzzleState>("game/block-shift-puzzle/reset");
    }

    public async resetPuzzle(canvas: HTMLCanvasElement): Promise<void> {
        await this.reset().then(state => this.draw(canvas, state.blocks));
    }

    public async moveBlock(canvas: HTMLCanvasElement, mouseX: number, mouseY: number): Promise<boolean> {
        const state: BlockShiftPuzzleState = await this.postState(mouseX, mouseY);
        this.draw(canvas, state.blocks);
        // Return whether the player has solved the puzzle
        return state.solved;
    }

    public async refreshPuzzle(canvas: HTMLCanvasElement): Promise<void> {
        const state: BlockShiftPuzzleState = await this.getState();
        this.draw(canvas, state.blocks);
    }

    private drawSprite(context: CanvasRenderingContext2D, block: ShiftableBlockState): void {
        switch (block.type) {
            case 0:
                context.drawImage(this._image, 0, 64, 128, 64, block.x, block.y, 128, 64);
                break;
            case 1:
                context.drawImage(this._image, 0, 0, 128, 64, block.x, block.y, 128, 64);
                break;
            case 2:
                context.drawImage(this._image, 128, 0, 192, 64, block.x, block.y, 192, 64);
                break;
            case 3:
                context.drawImage(this._image, 128, 64, 64, 128, block.x, block.y, 64, 128);
                break;
            case 4:
                context.drawImage(this._image, 128, 192, 64, 192, block.x, block.y, 64, 192);
                break;
        }
    }

    private draw(canvas: HTMLCanvasElement, blocks: ShiftableBlockState[]): void {
        const context: CanvasRenderingContext2D = canvas.getContext("2d")!;
        // Clear the canvas
        context.clearRect(0, 0, canvas.width, canvas.height);
        // Draw all the block
        for (const block of blocks) {
            this.drawSprite(context, block);
        }
    }
}
